/**
 * WarRoomReviewContent — accountant Stage-6 "War Room" panel (extract-content).
 *
 * Role-shaped view of the six-agent deliberation for the forensic accountant:
 * an ATTESTATION GATE, not a deliberation viewer. Answers one question — "can I
 * sign my number, or what is the shortest dollar-ranked path to a number I can
 * sign?" Design synthesised from Fable + K3 + the War Room accountant agent
 * (docs/plans/2026-08-23-warroom-stage6-design-synthesis.md).
 *
 * Reuses the existing deliberation stack — ConsistencyBanner, IssueCard (which
 * brings its own inline HumanInputForm, author_role human:accountant),
 * TimelineView — plus the shared useDeliberationData hook and the deliberationApi
 * brief/resolution-path/vulnerability endpoints. Zero new backend endpoints.
 *
 * `showHeader` gates the page header so the standalone route and the bare
 * workspace-stage embed share one component (matches the other stage panels).
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import {
  Loader2,
  AlertTriangle,
  ArrowUpRight,
  ExternalLink,
  Download,
  ShieldAlert,
  Swords,
} from 'lucide-react';
import { FinancialValue } from '@/components/shared/FinancialValue';
import { formatCurrency } from '@/utils/financialFormat';
import { humanizeMethodId } from '@/utils/methodLabels';
import ConsistencyBanner from '@/components/deliberation/ConsistencyBanner';
import { IssueCard } from '@/components/deliberation/IssueCard';
import { TimelineView } from '@/components/deliberation/TimelineView';
import { useDeliberationData } from '@/hooks/useDeliberationData';
import { useCanonicalNet } from '@/hooks/useCanonicalNet';
import {
  getBrief,
  getResolutionPaths,
  getAgentDisplayName,
  getMetricLabel,
  extractSeatCards,
  WARROOM_COUNTERPARTS,
  WARROOM_ADVERSARY,
  type Brief,
  type ResolutionPath,
  type DisputedIssue,
  type DeliberationEvent,
  type CounterpartRole,
} from '@/services/deliberationApi';
import {
  SectionHeader,
  CounterpartHeroCard,
  AdversaryBriefCard,
  FullRoomDisclosure,
} from '@/components/war-room/CounterpartCards';

export interface WarRoomReviewContentProps {
  reportId?: string;
  showHeader?: boolean;
  /**
   * Human dashboard role. Drives which AI seat leads (hero) + whether the
   * adversary preview shows. Defaults to 'accountant' so existing callers and
   * the accountant War Room stage are unchanged.
   */
  role?: CounterpartRole;
}

/** Human-role → resolution-path party keys. Non-accountant roles see all paths
 * (DECISION 1: single-tenant, full picture) until a role-specific party map exists. */
const ROLE_PARTIES: Partial<Record<CounterpartRole, string[]>> = {
  accountant: ['accountant', 'forensic_accountant'],
};

/** Per-role eyebrow label for the stage header (when showHeader). */
const ROLE_EYEBROW: Record<CounterpartRole, string> = {
  accountant: 'ACCOUNTANT REVIEW',
  attorney: 'ATTORNEY REVIEW',
  adjuster: 'CARRIER REVIEW',
  data_specialist: 'DATA READINESS REVIEW',
};

/** Timeline events worth showing on the attestation record — the raw JSON /
 * tool-call chatter is cut; only adjudications, positions, and human turns. */
const ATTESTABLE_EVENT_TYPES = new Set([
  'position',
  'finding',
  'disagreement',
  'human_input',
  're_analysis',
]);

/** Canonical six-seat order for the ledger (forensic = the accountant's own seat, first). */
const SEAT_ORDER = [
  'ai:forensic',
  'ai:attorney',
  'ai:adjuster',
  'ai:defendant_attorney',
  'ai:document_auditor',
  'ai:data_specialist',
];

interface SeatRow {
  name: string;
  score: number | null;
  value: string | null;
  glyph: string; // ● agree · ◐ split · ○ dissent — consensus by shape, hueless (K3)
  blocked: boolean; // this seat raised an attestation blocker → semantic-red glyph
}

/** A middle-estimate 1-1-1 resolution the accountant must personally acknowledge
 * (their signature inherits it). Detected from the resolution method label. */
function isMiddleAdopted(issue: DisputedIssue): boolean {
  const m = issue.resolution?.method?.toLowerCase() || '';
  return m.includes('middle') || m.includes('median');
}

const card: React.CSSProperties = {
  backgroundColor: 'var(--color-bg-surface)',
  border: '1px solid var(--color-border)',
  borderRadius: 2,
};

export function WarRoomReviewContent({ reportId: reportIdProp, showHeader = true, role = 'accountant' }: WarRoomReviewContentProps) {
  const { reportId: reportIdParam } = useParams<{ reportId: string }>();
  const reportId = reportIdProp ?? reportIdParam;

  const { deliberation, summary, loading, error, analysisState, analysisMessage, refetch, triggerAnalysis } =
    useDeliberationData(reportId);
  const canon = useCanonicalNet(reportId);

  const [brief, setBrief] = useState<Brief | null>(null);
  const [paths, setPaths] = useState<Array<ResolutionPath & { issue_tag: string }>>([]);
  const [showAllItems, setShowAllItems] = useState(false);
  const [showTrail, setShowTrail] = useState(false);
  const [downloading, setDownloading] = useState(false);

  // Brief (headline + gate findings) + accountant-filtered resolution paths.
  useEffect(() => {
    if (!reportId) return;
    let cancelled = false;
    (async () => {
      const [briefRes, pathRes] = await Promise.all([
        getBrief(reportId).then((r) => r.brief).catch(() => null),
        getResolutionPaths(reportId).catch(() => null),
      ]);
      if (cancelled) return;
      setBrief(briefRes);
      if (pathRes) {
        // Accountant sees only its own party's paths; other roles see all
        // (DECISION 1: single-tenant, full picture) until a role party map exists.
        const keys = ROLE_PARTIES[role] ?? Object.keys(pathRes.by_party);
        const mine: Array<ResolutionPath & { issue_tag: string }> = [];
        for (const key of keys) {
          const arr = pathRes.by_party[key];
          if (arr) mine.push(...arr);
        }
        mine.sort((a, b) => (parseFloat(b.impact_value || '0') || 0) - (parseFloat(a.impact_value || '0') || 0));
        setPaths(mine);
      }
    })();
    return () => { cancelled = true; };
  }, [reportId, deliberation, role]);

  const openIssues = useMemo(
    () => (deliberation?.issues || []).filter((i) => i.status === 'open'),
    [deliberation],
  );

  const attestableEvents = useMemo<DeliberationEvent[]>(
    () => (deliberation?.events || []).filter((e) => ATTESTABLE_EVENT_TYPES.has(e.event_type)),
    [deliberation],
  );

  // Six-seat ledger — score + headline $ per seat from the deliberation events;
  // consensus state shown by glyph shape only, no hue (K3 render review #4).
  const seatRows = useMemo<SeatRow[]>(() => {
    const events = deliberation?.events || [];
    const issues = deliberation?.issues || [];
    // Seats that raised an attestation blocker (e.g. Document Auditor → document_health).
    const blockerSeats = new Set(
      (brief?.gate_findings || []).filter((f) => f.severity === 'blocker').flatMap((f) => f.seats || []),
    );
    return SEAT_ORDER.map((name) => {
      const evs = events.filter((e) => e.agent_name === name);
      if (evs.length === 0) return null;
      const last = evs[evs.length - 1];
      const blocked = blockerSeats.has(name);
      const inOpenIssue = issues.some(
        (i) => i.status === 'open' && i.agent_positions && i.agent_positions[name],
      );
      // blocker → ○ (semantic red); adversarial defense → ○; in an open split → ◐; else agree ●.
      const glyph = blocked || name === 'ai:defendant_attorney' ? '○' : inOpenIssue ? '◐' : '●';
      return {
        name,
        score: last.confidence != null ? Math.round(last.confidence * 100) : null,
        value: last.value,
        glyph,
        blocked,
      };
    }).filter((r): r is SeatRow => r !== null);
  }, [deliberation, brief]);

  // Counterpart seats for the role-scoped hero/adversary (K3) — reuse the SAME
  // deliberation + brief already fetched above (no re-fetch).
  const seatCards = useMemo(() => extractSeatCards(deliberation, brief), [deliberation, brief]);
  const heroSeatNames = WARROOM_COUNTERPARTS[role] || [];
  const heroSeats = heroSeatNames
    .map((n) => seatCards[n])
    .filter((s): s is NonNullable<typeof s> => !!s);
  const adversaryName = WARROOM_ADVERSARY[role];
  const adversarySeat = adversaryName ? seatCards[adversaryName] ?? null : null;

  const blockers = (brief?.gate_findings || []).filter((f) => f.severity === 'blocker');
  const warnings = (brief?.gate_findings || []).filter((f) => f.severity === 'warning');
  const gateBlocked = blockers.length > 0 || deliberation?.consistency_report?.blocked === true;

  // ── Header ────────────────────────────────────────────────────────────────
  const header = showHeader ? (
    <div className="mb-6 max-w-[1100px] mx-auto">
      <div className="cs-eyebrow flex items-center gap-2 mb-1">
        <span>{ROLE_EYEBROW[role]}</span><span>•</span><span>WAR ROOM</span>
      </div>
      <h1 className="cs-display">War Room · Attestation Gate</h1>
    </div>
  ) : null;

  // ── Loading / error / empty ─────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="p-6 lg:p-8">
        {header}
        <div className="flex items-center justify-center py-16" data-testid="warroom-review-loading">
          <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--color-text-dim)' }} />
        </div>
      </div>
    );
  }

  if (error && !deliberation) {
    return (
      <div className="p-6 lg:p-8">
        {header}
        <div
          className="max-w-[1100px] mx-auto p-4 text-sm flex items-start gap-3 rounded-[2px]"
          style={{ backgroundColor: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)' }}
          data-testid="warroom-review-error"
        >
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: 'var(--color-blocker)' }} />
          <div className="flex-1">
            <p className="font-medium" style={{ color: 'var(--color-blocker)' }}>Could not load the War Room</p>
            <p className="mt-1 text-xs" style={{ color: 'var(--color-text-secondary)' }}>{error}</p>
          </div>
          <button onClick={refetch} className="text-xs underline flex-shrink-0" style={{ color: 'var(--color-brand)' }}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  // No deliberation has run yet → the trigger flow.
  if (!deliberation || (deliberation.events.length === 0 && deliberation.issues.length === 0 && !brief)) {
    const busy = analysisState === 'triggering' || analysisState === 'running';
    return (
      <div className="p-6 lg:p-8">
        {header}
        <div className="max-w-[1100px] mx-auto border-2 border-dashed rounded-[2px] p-8 text-center"
          style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-elevated)' }}
          data-testid="warroom-review-empty">
          <Swords className="w-7 h-7 mx-auto mb-3" style={{ color: 'var(--color-text-muted)' }} />
          <p className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>
            No War Room deliberation yet
          </p>
          <p className="text-xs mt-1 mb-4 leading-relaxed" style={{ color: 'var(--color-text-secondary)' }}>
            Run the six-agent deliberation to stress-test your number before you attest.
          </p>
          {busy ? (
            <p className="text-xs inline-flex items-center gap-2" style={{ color: 'var(--color-needs-human)' }}>
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> {analysisMessage}
            </p>
          ) : (
            <button
              onClick={triggerAnalysis}
              className="text-sm font-medium inline-flex items-center gap-2 rounded-[2px] px-4 py-2"
              style={{ backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
            >
              <Swords className="w-4 h-4" /> Run War Room analysis
            </button>
          )}
          {analysisState === 'failed' && (
            <p className="text-xs mt-3" style={{ color: 'var(--color-blocker)' }}>{analysisMessage}</p>
          )}
        </div>
      </div>
    );
  }

  const h = brief?.headline;
  const yourNumber = h?.recommended_recovery_p50 ?? h?.bi_total_loss ?? null;
  const rangeLow = h?.defensible_range?.low ?? null;
  const rangeHigh = h?.defensible_range?.high ?? null;
  const ceiling = h?.supportable_ceiling ?? null;
  // Position the "your number" tick within the defensible band (0..100%).
  const pctIn = (v: number | null): number | null =>
    v !== null && rangeLow !== null && rangeHigh !== null && rangeHigh > rangeLow
      ? Math.max(0, Math.min(100, ((v - rangeLow) / (rangeHigh - rangeLow)) * 100))
      : null;
  const tickPct = pctIn(yourNumber);
  // Ceiling gets its own hueless marker ON the band (K3 render review #3); the
  // carrier/adjuster figure isn't in the brief payload, so it's omitted for now.
  const ceilingPct = pctIn(ceiling);

  return (
    <div className="p-6 lg:p-8">
      {header}
      <div className="max-w-[1100px] mx-auto space-y-6">
        {/* Stage header strip + exhibit stamp (K3 §1A) */}
        <div className="flex items-center justify-between gap-3 pb-3" style={{ borderBottom: '1px solid var(--color-border)' }}>
          <span className="cs-display" style={{ fontSize: 20 }}>War Room</span>
          <div className="flex items-center gap-3 min-w-0">
            {/* Status cluster (K3 §1A): seats returned · run id · completed-at */}
            <span className="text-[11px] font-mono tabular-nums truncate hidden sm:inline" style={{ color: 'var(--color-text-dim)' }}>
              {seatRows.length ? `${seatRows.length} SEATS` : ''}
              {brief?.run_id ? ` · ${brief.run_id}` : ''}
              {brief?.generated_at ? ` · ${brief.generated_at}` : ''}
            </span>
            <span
              className="text-[11px] font-mono uppercase tracking-wider px-2 py-0.5 flex-shrink-0"
              style={{ color: 'var(--color-text-dim)', border: '1px solid var(--color-border)', borderRadius: 2 }}
            >
              Draft — Unverified
            </span>
          </div>
        </div>

        {/* HERO — your AI counterpart(s) (K3 §1B) */}
        {heroSeats.length > 0 ? (
          <div className={heroSeats.length > 1 ? 'grid gap-4 xl:grid-cols-2' : ''}>
            {heroSeats.map((s) => (
              <CounterpartHeroCard
                key={s.seat}
                seat={s}
                preSynthesis={role === 'attorney'}
                runId={brief?.run_id ?? undefined}
                completedAt={brief?.generated_at ?? undefined}
              />
            ))}
          </div>
        ) : (
          heroSeatNames.length > 0 && (
            <CounterpartHeroCard seat={null} placeholderName={getAgentDisplayName(heroSeatNames[0])} pending />
          )
        )}

        {/* ADVERSARY — defense attack preview (K3 §1C; attorney + adjuster) */}
        {adversaryName && (
          <AdversaryBriefCard seat={adversarySeat} counterStrategy={brief?.counter_strategy} />
        )}

        {/* CONSENSUS — where the room landed (K3 §1D). The existing composite follows. */}
        <SectionHeader title="Consensus" micro={brief?.run_id ? `from brief · ${brief.run_id}` : undefined} />

        {/* §0 — Consistency gate (renders nothing when clean) */}
        <ConsistencyBanner report={deliberation.consistency_report} status={deliberation.consistency_status} />

        {/* Six-seat ledger — provenance at the point of attestation (K3 #4).
            Hueless; consensus by glyph shape; whole band links to the full War Room. */}
        {seatRows.length > 1 && (
          <Link
            to={`/deliberation/${reportId}`}
            className="block rounded-[2px] overflow-hidden"
            style={{ backgroundColor: 'var(--color-bg-base)', border: '1px solid var(--color-border)' }}
            data-testid="warroom-seat-ledger"
          >
            <div className="grid" style={{ gridTemplateColumns: `repeat(${seatRows.length}, minmax(0, 1fr))` }}>
              {seatRows.map((s, i) => (
                <div key={s.name} className="px-3 py-2"
                  style={i > 0 ? { borderLeft: '1px solid var(--color-border-sub)' } : undefined}>
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs" style={{ color: s.blocked ? 'var(--color-blocker)' : 'var(--color-text-muted)' }} aria-hidden="true">{s.glyph}</span>
                    <span className="text-[10px] uppercase tracking-wide truncate" style={{ color: 'var(--color-text-muted)' }}>
                      {getAgentDisplayName(s.name)}
                    </span>
                  </div>
                  <p className="text-[9px] mt-0.5" style={{ color: 'var(--color-text-dim)' }}>{getMetricLabel(s.name)}</p>
                  <p className="text-[13px] font-mono tabular-nums mt-0.5" style={{ color: 'var(--color-text-secondary)' }}>
                    {s.score ?? '—'}{s.value ? ` · ${formatCurrency(parseFloat(s.value))}` : ''}
                  </p>
                </div>
              ))}
            </div>
          </Link>
        )}

        {/* §1 — YOUR NUMBER + field of play */}
        {(h || canon.net != null) && (
          <section style={card} className="p-5" data-testid="warroom-number">
            {/* Canonical net — headline (unconditional slot) */}
            <div data-testid="warroom-net-headline">
              <p className="cs-label mb-2">Net supported claim</p>
              {canon.loading ? (
                <p className="text-sm font-mono tabular-nums" style={{ color: 'var(--color-text-dim)' }} data-testid="warroom-net-loading">
                  Loading…
                </p>
              ) : canon.net != null ? (
                <>
                  <p className="cs-figure-lg" style={{ color: 'var(--color-text-primary)', fontWeight: 700, fontSize: '1.75rem' }}>
                    <FinancialValue value={canon.net} />
                  </p>
                  {canon.computedAt && (
                    <p className="text-xs mt-1 font-mono tabular-nums" style={{ color: 'var(--color-text-muted)' }}>
                      policy_deductions · engine run {canon.computedAt.slice(0, 10)}
                    </p>
                  )}
                  {canon.electedMethod && canon.electedMethod !== 'reconciled' && (
                    <p className="text-xs mt-1 font-mono tabular-nums" style={{ color: 'var(--color-text-muted)' }}>
                      Basis: {humanizeMethodId(canon.electedMethod)} method (attorney-elected)
                    </p>
                  )}
                </>
              ) : (
                <>
                  <p className="cs-figure-lg" style={{ color: 'var(--color-text-dim)', fontWeight: 700, fontSize: '1.75rem' }}>
                    —
                  </p>
                  <p className="text-xs mt-1 font-mono tabular-nums" style={{ color: 'var(--color-text-muted)' }}>
                    Canonical net unavailable
                  </p>
                </>
              )}
            </div>
            {/* Secondary figure — demoted from headline */}
            {yourNumber != null && (
              <div className="mt-3">
                <p className="text-xs font-mono uppercase tracking-wide" style={{ color: 'var(--color-text-dim)' }}>
                  {h?.recommended_recovery_p50 != null
                    ? 'Recommended recovery (fair-settlement P50)'
                    : 'Bottom-up BI loss (pre-deduction)'}
                </p>
                <p className="text-[1.125rem] font-mono tabular-nums mt-0.5" style={{ color: 'var(--color-text-secondary)' }} data-testid="warroom-secondary-figure">
                  <FinancialValue value={yourNumber} />
                </p>
              </div>
            )}
            {h && (
              <div className="flex items-end justify-between flex-wrap gap-3 mt-3">
                <div>
                  <p className="text-xs mt-1 font-mono tabular-nums" style={{ color: 'var(--color-text-muted)' }}>
                    defensible <FinancialValue value={rangeLow} /> – <FinancialValue value={rangeHigh} />
                    {h.defensible_range?.methodology ? ` · ${h.defensible_range.methodology}` : ''}
                  </p>
                </div>
                {ceiling !== null && (
                  <div className="text-right">
                    <p className="cs-label">{h?.supportable_ceiling_label || 'BI ceiling (forensic high)'}</p>
                    <p className="cs-figure font-mono tabular-nums" style={{ color: 'var(--color-text-secondary)' }}><FinancialValue value={ceiling} /></p>
                  </div>
                )}
              </div>
            )}
            {/* Field-of-play band: defensible range as the axis. Secondary tick is grayscale
                (fg ~85%) — orange is reserved for the hero rail only (K3 three-hue budget). */}
            {tickPct !== null && (
              <div className="mt-5">
                <div className="relative h-1.5 rounded-[2px]" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
                  {/* Ceiling — hueless marker + caption */}
                  {ceilingPct !== null && (
                    <div className="absolute -top-4 -translate-x-1/2 text-[9px] whitespace-nowrap" style={{ left: `${ceilingPct}%`, color: 'var(--color-text-dim)' }}>
                      ceiling
                    </div>
                  )}
                  {ceilingPct !== null && (
                    <div
                      className="absolute top-1/2 -translate-y-1/2 w-0.5 h-2.5"
                      style={{ left: `${ceilingPct}%`, backgroundColor: 'var(--color-text-muted)' }}
                      aria-label="supportable ceiling"
                    />
                  )}
                  {/* Secondary figure — grayscale tick (fg ~85%), brighter than the ceiling
                      marker so hierarchy still distinguishes them. No orange (K3 §4). */}
                  <div className="absolute -top-4 -translate-x-1/2 text-[9px] whitespace-nowrap" style={{ left: `${tickPct}%`, color: 'var(--color-text-dim)' }}>
                    {h?.recommended_recovery_p50 != null ? 'P50' : 'bottom-up'}
                  </div>
                  <div
                    className="absolute top-1/2 -translate-y-1/2 w-0.5 h-3.5"
                    style={{ left: `${tickPct}%`, backgroundColor: 'var(--color-text-secondary)' }}
                    aria-label="secondary figure"
                  />
                </div>
                <div className="flex justify-between text-[10px] mt-1 font-mono tabular-nums" style={{ color: 'var(--color-text-dim)' }}>
                  <span><FinancialValue value={rangeLow} /></span>
                  <span><FinancialValue value={rangeHigh} /></span>
                </div>
              </div>
            )}
          </section>
        )}

        {/* §2 — YOUR PRICED QUEUE (the working core) */}
        <section style={card} className="p-5" data-testid="warroom-queue">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <p className="cs-label">
              {role === 'accountant' ? 'Blocking your signature' : 'Open items'}{paths.length ? ` — ${paths.length} item${paths.length > 1 ? 's' : ''}, ranked by $` : ''}
            </p>
            {openIssues.length > 0 && (
              <button
                onClick={() => setShowAllItems((v) => !v)}
                className="text-xs rounded-[2px] px-2 py-1"
                style={{ border: '1px solid var(--color-border-sub)', color: 'var(--color-text-muted)' }}
              >
                {showAllItems ? `All disputed (${openIssues.length})` : `Mine (${paths.length})`}
              </button>
            )}
          </div>

          {/* Ranked accountant resolution paths (priced) */}
          {paths.length > 0 && (
            <ul className="mb-4 divide-y" style={{ borderColor: 'var(--color-border-faint)' }}>
              {paths.map((p, i) => (
                <li key={i} className="py-2.5 flex items-start justify-between gap-4">
                  <div className="flex items-start gap-2 min-w-0">
                    <span className="cs-figure text-xs mt-0.5" style={{ color: 'var(--color-text-dim)' }}>{i + 1}</span>
                    <div className="min-w-0">
                      <p className="text-sm" style={{ color: 'var(--color-text-primary)' }}>{p.action}</p>
                      {p.impact_description && (
                        <p className="text-xs mt-0.5" style={{ color: 'var(--color-text-muted)' }}>{p.impact_description}</p>
                      )}
                    </div>
                  </div>
                  {p.impact_value && (
                    <span className="cs-figure font-mono tabular-nums text-sm flex-shrink-0" style={{ color: 'var(--color-text-secondary)' }}>
                      <FinancialValue value={parseFloat(p.impact_value)} />
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}

          {/* Open disputed issues — reuse IssueCard (inline HumanInputForm, author accountant) */}
          {openIssues.length > 0 ? (
            <div className="space-y-3">
              {openIssues.map((issue) => (
                <div key={issue.tag} data-testid="warroom-issue">
                  <IssueCard issue={issue} reportId={reportId!} onInputSubmitted={refetch} />
                  {isMiddleAdopted(issue) && (
                    <div className="flex items-center gap-1.5 text-xs mt-1 py-1">
                      <span className="font-mono" style={{ color: 'var(--color-text-dim)' }}>MIDDLE ADOPTED</span>
                      <span style={{ color: 'var(--color-text-muted)' }}>— acknowledge on the record</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            paths.length === 0 && (
              <p className="text-sm" style={{ color: 'var(--color-verified)' }}>
                No open items assigned to you — {role === 'accountant' ? 'nothing blocks your signature' : 'nothing is unresolved for you'} in the deliberation.
              </p>
            )
          )}
        </section>

        {/* §3 — ATTESTATION GATE (+ verdict footer bound to it, per K3) */}
        <section style={card} className="p-5" data-testid="warroom-readiness">
          <p className="cs-label mb-3">{role === 'accountant' ? 'Attestation gate' : 'Consensus gate'} · consistency-gate findings</p>
          {blockers.length > 0 || warnings.length > 0 ? (
            <ul className="space-y-2">
              {blockers.map((f, i) => (
                <li key={`b${i}`} className="flex items-start gap-2 text-sm"
                  style={{ boxShadow: 'inset 2px 0 0 var(--color-blocker)', paddingLeft: 10 }}>
                  <ShieldAlert className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: 'var(--color-blocker)' }} />
                  <div>
                    <span className="font-medium" style={{ color: 'var(--color-blocker)' }}>CONSISTENCY-GATE BLOCKER · {f.fact_key}</span>
                    <p className="text-xs mt-0.5" style={{ color: 'var(--color-text-secondary)' }}>{f.detail}</p>
                  </div>
                </li>
              ))}
              {warnings.map((f, i) => (
                <li key={`w${i}`} className="flex items-start gap-2 text-sm">
                  <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: 'var(--color-text-muted)' }} />
                  <div>
                    <span className="font-medium" style={{ color: 'var(--color-text-primary)' }}>{f.fact_key}</span>
                    <p className="text-xs mt-0.5" style={{ color: 'var(--color-text-muted)' }}>{f.detail}</p>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm" style={{ color: 'var(--color-verified)' }}>
              No blockers — nothing in the deliberation stops your signature.
            </p>
          )}
          {/* Verdict — the consequence of the gate, bound to it */}
          <div className="mt-4 pt-4 flex items-center justify-between flex-wrap gap-3" style={{ borderTop: '1px solid var(--color-border-sub)' }}>
            <span className="text-sm inline-flex items-center gap-2">
              {gateBlocked ? (
                <>
                  <ShieldAlert className="w-4 h-4" style={{ color: 'var(--color-blocker)' }} />
                  <span style={{ color: 'var(--color-blocker)' }}>
                    {role === 'accountant' ? 'Sign-off blocked — clear the gate above' : 'Blocked — clear the gate above'}
                  </span>
                </>
              ) : (
                <span style={{ color: 'var(--color-verified)' }}>
                  {role === 'accountant' ? 'Your number is ready to attest' : 'No blockers — the number is clear'}
                </span>
              )}
            </span>
            {role === 'accountant' && !gateBlocked && (
              <Link
                to={`/dashboard/accountant/case/${reportId}`}
                className="text-sm font-medium inline-flex items-center gap-1.5 rounded-[2px] px-4 py-2"
                style={{ backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
              >
                Proceed to Sign-Off <ArrowUpRight className="w-4 h-4" />
              </Link>
            )}
          </div>
        </section>

        {/* §5 — Deliberation record (witness trail) */}
        <section style={card} className="p-5" data-testid="warroom-trail">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <button onClick={() => setShowTrail((v) => !v)} className="cs-label inline-flex items-center gap-1.5">
              <span>Deliberation record</span>
              <span className="font-mono tabular-nums" style={{ color: 'var(--color-text-dim)' }}>
                {summary ? `${summary.total_events} events · ${summary.human_inputs} inputs · ${summary.re_analyses_triggered} re-analyses` : ''}
              </span>
            </button>
            <div className="flex items-center gap-3">
              {reportId && (
                <a
                  href={`/api/v1/deliberation/${reportId}/brief?format=md`}
                  onClick={(e) => { e.preventDefault(); if (!downloading) { setDownloading(true); import('@/services/deliberationApi').then((m) => m.downloadBriefMarkdown(reportId!)).finally(() => setDownloading(false)); } }}
                  className="text-xs inline-flex items-center gap-1" style={{ color: 'var(--color-text-muted)' }}
                >
                  <Download className="w-3 h-3" /> Brief (.md)
                </a>
              )}
              <Link to={`/deliberation/${reportId}`} className="text-xs inline-flex items-center gap-1" style={{ color: 'var(--color-text-muted)' }}>
                Open full War Room <ExternalLink className="w-3 h-3" />
              </Link>
            </div>
          </div>
          {showTrail && (
            <div className="mt-4 max-h-96 overflow-y-auto">
              {attestableEvents.length > 0 ? (
                <TimelineView events={attestableEvents} />
              ) : (
                <p className="text-xs" style={{ color: 'var(--color-text-dim)' }}>No attestable events recorded yet.</p>
              )}
            </div>
          )}
        </section>

        {/* FULL ROOM — every seat, collapsed by default (K3 §1E) */}
        <FullRoomDisclosure seatCards={seatCards} heroSeats={heroSeatNames} order={SEAT_ORDER} />

      </div>
    </div>
  );
}

export default WarRoomReviewContent;
